import { useEffect } from 'react';
import {
  useContactApi,
  useContactCreateApi,
  useContactUpdateApi,
  UseContactCreateApiBody,
} from '@hero/api-hooks';
import { Contact, getObjectId, Id } from '@hero/schema';
import { useContactCreateForm } from './useContactForm';

export interface UseContactCreateAdapterOptions {
  onContactSaved?: (contact: Contact) => void;
  /** When set, load contact for edit; when undefined, create mode. */
  contactId?: Id;
  /** For create mode: optional customer id to pre-fill. */
  customerId?: Id;
  /** Optional initial form values (e.g. pre-fill from context). Overwritten when loading an item via contactId. */
  defaultValues?: Partial<UseContactCreateApiBody>;
}

export const useContactCreateAdapter = (options: UseContactCreateAdapterOptions) => {
  const { contactId, onContactSaved } = options;
  const contactQuery = useContactApi(contactId);
  const createContact = useContactCreateApi();
  const updateContact = useContactUpdateApi();
  const savedContact = contactQuery.data;

  async function onSubmit(values: UseContactCreateApiBody) {
    try {
      if (savedContact) {
        const result = await updateContact.mutateAsync({
          _id: getObjectId(savedContact._id),
          name: values.name,
        });
        onContactSaved?.(result);
      } else {
        const result = await createContact.mutateAsync(values);
        onContactSaved?.(result);
      }
    } catch (error) {
      console.error('Failed to save contact:', error);
    }
  }

  const formHook = useContactCreateForm({
    defaultValues: options.defaultValues,
    onSubmit,
  });

  // When we have an id and data loaded, pass the entity to the form so it can set its values
  useEffect(() => {
    if (contactId != null && savedContact) {
      formHook.setContact(savedContact);
    }
  }, [contactId, savedContact, formHook.setContact]);

  const reset = () => {
    createContact.reset();
    updateContact.reset();
    if (savedContact) {
      formHook.setContact(savedContact);
    } else {
      formHook.reset();
    }
  };

  const onSaveClick = () => {
    formHook.onValidSubmit();
  };

  return {
    contactQuery,
    createContact,
    updateContact,
    formHook,
    reset,
    onSaveClick,
    savedContact,
  };
};
